import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Search,
  UserPlus,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  GraduationCap,
  Users,
  AlertTriangle
} from 'lucide-react';
import { schoolInfo } from '../utils/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Class options for dropdown
const CLASSES = ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12'];
const SECTIONS = ['A', 'B', 'C', 'D'];

const StudentsManagementPage = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Selected student for edit/delete
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    student_name: '',
    class: '',
    section: '',
    roll_number: '',
    parent_name: '',
    mobile_number: '',
    email: '',
    address: '',
    teacher_name: '',
    total_fees: '',
    paid_fees: ''
  });
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    verifyAdminAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyAdminAndFetch = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      await axios.get(`${BACKEND_URL}/api/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchStudents();
    } catch (err) {
      navigate('/admin/login');
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/students`);
      setStudents(response.data);
      setFilteredStudents(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching students:', err);
      toast.error('Failed to load students');
      setLoading(false);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    filterStudents(term, filterClass);
  };

  const handleClassFilter = (className) => {
    setFilterClass(className);
    filterStudents(searchTerm, className);
  };

  const filterStudents = (term, className) => {
    let filtered = students;

    // Filter by search term
    if (term) {
      filtered = filtered.filter(student =>
        student.student_name.toLowerCase().includes(term.toLowerCase()) ||
        student.class_name.toLowerCase().includes(term.toLowerCase()) ||
        student.parent_name.toLowerCase().includes(term.toLowerCase()) ||
        student.mobile_number.includes(term) ||
        (student.roll_number && student.roll_number.includes(term))
      );
    }

    // Filter by class
    if (className) {
      filtered = filtered.filter(student =>
        student.class_name.includes(className)
      );
    }

    setFilteredStudents(filtered);
  };

  const resetForm = () => {
    setFormData({
      student_name: '',
      class: '',
      section: '',
      roll_number: '',
      parent_name: '',
      mobile_number: '',
      email: '',
      address: '',
      teacher_name: '',
      total_fees: '',
      paid_fees: ''
    });
  };

  const handleAddStudent = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEditStudent = (student) => {
    setSelectedStudent(student);
    
    // Parse class_name to extract class and section
    // Format: "Class 5-A" -> class: "Class 5", section: "A"
    let classValue = '';
    let sectionValue = '';
    
    const match = student.class_name.match(/^(Class \d{1,2})-([A-D])$/);
    if (match) {
      classValue = match[1];
      sectionValue = match[2];
    } else {
      classValue = student.class_name;
      sectionValue = 'A';
    }
    
    setFormData({
      student_name: student.student_name,
      class: classValue,
      section: sectionValue,
      roll_number: student.roll_number || '',
      parent_name: student.parent_name,
      mobile_number: student.mobile_number,
      email: student.email || '',
      address: student.address || '',
      teacher_name: student.teacher_name || '',
      total_fees: student.fees?.total || '',
      paid_fees: student.fees?.paid || ''
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (student) => {
    setSelectedStudent(student);
    setShowDeleteModal(true);
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (!formData.student_name.trim()) {
      toast.error('Student name is required');
      return false;
    }
    if (!formData.class) {
      toast.error('Please select a class');
      return false;
    }
    if (!formData.section) {
      toast.error('Please select a section');
      return false;
    }
    if (!formData.parent_name.trim()) {
      toast.error('Parent name is required');
      return false;
    }
    if (!formData.mobile_number.trim()) {
      toast.error('Mobile number is required');
      return false;
    }
    if (formData.mobile_number.length !== 10 || !/^\d{10}$/.test(formData.mobile_number)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return false;
    }
    return true;
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSubmitting(true);
    
    try {
      // Construct class_name in format: "Class X-Y"
      const class_name = `${formData.class}-${formData.section}`;
      
      await axios.post(`${BACKEND_URL}/api/students`, {
        student_name: formData.student_name,
        class_name: class_name,
        parent_name: formData.parent_name,
        mobile_number: formData.mobile_number,
        roll_number: formData.roll_number || '',
        email: formData.email || '',
        address: formData.address || '',
        teacher_name: formData.teacher_name || '',
        fees_total: parseFloat(formData.total_fees) || 0,
        fees_paid: parseFloat(formData.paid_fees) || 0
      });

      toast.success('Student added successfully!');
      setShowAddModal(false);
      resetForm();
      await fetchStudents();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to add student';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSubmitting(true);
    
    try {
      // Construct class_name in format: "Class X-Y"
      const class_name = `${formData.class}-${formData.section}`;
      
      await axios.put(`${BACKEND_URL}/api/students/${selectedStudent.id}`, {
        student_name: formData.student_name,
        class_name: class_name,
        parent_name: formData.parent_name,
        mobile_number: formData.mobile_number,
        roll_number: formData.roll_number || '',
        email: formData.email || '',
        address: formData.address || '',
        teacher_name: formData.teacher_name || '',
        fees_total: parseFloat(formData.total_fees) || 0,
        fees_paid: parseFloat(formData.paid_fees) || 0
      });

      toast.success('Student updated successfully!');
      setShowEditModal(false);
      setSelectedStudent(null);
      await fetchStudents();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update student';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedStudent) return;

    setSubmitting(true);
    
    try {
      await axios.delete(`${BACKEND_URL}/api/students/${selectedStudent.id}`);
      toast.success('Student deleted successfully!');
      setShowDeleteModal(false);
      setSelectedStudent(null);
      await fetchStudents();
    } catch (error) {
      toast.error('Failed to delete student');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading students...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-md border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Student Management</h1>
                <p className="text-sm text-gray-600">{filteredStudents.length} students</p>
              </div>
            </div>
            <Button
              onClick={handleAddStudent}
              className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add Student
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Search and Filter */}
        <Card className="mb-6 shadow-lg">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Search */}
              <div>
                <Label htmlFor="search">Search Students</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="search"
                    type="text"
                    placeholder="Name, class, mobile, roll no..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Filter by Class */}
              <div>
                <Label htmlFor="filter-class">Filter by Class</Label>
                <select
                  id="filter-class"
                  value={filterClass}
                  onChange={(e) => handleClassFilter(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Classes</option>
                  {CLASSES.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Students Table */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-white">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              All Students
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {filteredStudents.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg mb-2">No students found</p>
                <p className="text-sm text-gray-400">Add your first student to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Class</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Roll No</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Parent</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Mobile</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredStudents.map((student, index) => (
                      <tr key={student.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4 text-sm text-gray-900">{index + 1}</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <div className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                              {student.student_name.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium text-gray-900">{student.student_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            <GraduationCap className="h-3 w-3 mr-1" />
                            {student.class_name}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">{student.roll_number || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-900">{student.parent_name}</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Phone className="h-3 w-3" />
                            {student.mobile_number}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditStudent(student)}
                              className="border-blue-600 text-blue-600 hover:bg-blue-50"
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteClick(student)}
                              className="border-red-600 text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Student Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-green-600" />
              Add New Student
            </DialogTitle>
            <DialogDescription>
              Fill in the student details below. Fields marked with * are required.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitAdd} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Student Name */}
              <div>
                <Label htmlFor="student_name">Student Name *</Label>
                <Input
                  id="student_name"
                  name="student_name"
                  value={formData.student_name}
                  onChange={handleFormChange}
                  placeholder="Enter student name"
                  required
                />
              </div>

              {/* Parent Name */}
              <div>
                <Label htmlFor="parent_name">Parent Name *</Label>
                <Input
                  id="parent_name"
                  name="parent_name"
                  value={formData.parent_name}
                  onChange={handleFormChange}
                  placeholder="Enter parent name"
                  required
                />
              </div>

              {/* Class */}
              <div>
                <Label htmlFor="class">Class *</Label>
                <select
                  id="class"
                  name="class"
                  value={formData.class}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Class</option>
                  {CLASSES.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              {/* Section */}
              <div>
                <Label htmlFor="section">Section *</Label>
                <select
                  id="section"
                  name="section"
                  value={formData.section}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Section</option>
                  {SECTIONS.map(sec => (
                    <option key={sec} value={sec}>{sec}</option>
                  ))}
                </select>
              </div>

              {/* Roll Number */}
              <div>
                <Label htmlFor="roll_number">Roll Number</Label>
                <Input
                  id="roll_number"
                  name="roll_number"
                  value={formData.roll_number}
                  onChange={handleFormChange}
                  placeholder="Enter roll number"
                />
              </div>

              {/* Mobile Number */}
              <div>
                <Label htmlFor="mobile_number">Mobile Number *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="mobile_number"
                    name="mobile_number"
                    value={formData.mobile_number}
                    onChange={handleFormChange}
                    placeholder="10-digit mobile number"
                    maxLength="10"
                    className="pl-10"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Used for parent login</p>
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email">Email (Optional)</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    placeholder="Enter email address"
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Teacher Name */}
              <div>
                <Label htmlFor="teacher_name">Class Teacher (Optional)</Label>
                <Input
                  id="teacher_name"
                  name="teacher_name"
                  value={formData.teacher_name}
                  onChange={handleFormChange}
                  placeholder="Enter teacher name"
                />
              </div>

              {/* Total Fees */}
              <div>
                <Label htmlFor="total_fees">Total Fees (₹)</Label>
                <Input
                  id="total_fees"
                  name="total_fees"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.total_fees}
                  onChange={handleFormChange}
                  placeholder="Enter total fees"
                />
              </div>

              {/* Paid Fees */}
              <div>
                <Label htmlFor="paid_fees">Paid Fees (₹)</Label>
                <Input
                  id="paid_fees"
                  name="paid_fees"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.paid_fees}
                  onChange={handleFormChange}
                  placeholder="Enter paid amount"
                />
              </div>
            </div>

            {/* Due Fees Display */}
            {(formData.total_fees || formData.paid_fees) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Due Fees:</span>
                  <span className="text-xl font-bold text-blue-600">
                    ₹{(parseFloat(formData.total_fees || 0) - parseFloat(formData.paid_fees || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* Address */}
            <div>
              <Label htmlFor="address">Address (Optional)</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleFormChange}
                  placeholder="Enter complete address"
                  rows={3}
                  className="pl-10"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-green-600 hover:bg-green-700"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save Student'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Student Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-600" />
              Edit Student Details
            </DialogTitle>
            <DialogDescription>
              Update student information below. Fields marked with * are required.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitEdit} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Student Name */}
              <div>
                <Label htmlFor="edit_student_name">Student Name *</Label>
                <Input
                  id="edit_student_name"
                  name="student_name"
                  value={formData.student_name}
                  onChange={handleFormChange}
                  placeholder="Enter student name"
                  required
                />
              </div>

              {/* Parent Name */}
              <div>
                <Label htmlFor="edit_parent_name">Parent Name *</Label>
                <Input
                  id="edit_parent_name"
                  name="parent_name"
                  value={formData.parent_name}
                  onChange={handleFormChange}
                  placeholder="Enter parent name"
                  required
                />
              </div>

              {/* Class */}
              <div>
                <Label htmlFor="edit_class">Class *</Label>
                <select
                  id="edit_class"
                  name="class"
                  value={formData.class}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Class</option>
                  {CLASSES.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              {/* Section */}
              <div>
                <Label htmlFor="edit_section">Section *</Label>
                <select
                  id="edit_section"
                  name="section"
                  value={formData.section}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Section</option>
                  {SECTIONS.map(sec => (
                    <option key={sec} value={sec}>{sec}</option>
                  ))}
                </select>
              </div>

              {/* Roll Number */}
              <div>
                <Label htmlFor="edit_roll_number">Roll Number</Label>
                <Input
                  id="edit_roll_number"
                  name="roll_number"
                  value={formData.roll_number}
                  onChange={handleFormChange}
                  placeholder="Enter roll number"
                />
              </div>

              {/* Mobile Number */}
              <div>
                <Label htmlFor="edit_mobile_number">Mobile Number *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="edit_mobile_number"
                    name="mobile_number"
                    value={formData.mobile_number}
                    onChange={handleFormChange}
                    placeholder="10-digit mobile number"
                    maxLength="10"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="edit_email">Email (Optional)</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="edit_email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    placeholder="Enter email address"
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Teacher Name */}
              <div>
                <Label htmlFor="edit_teacher_name">Class Teacher (Optional)</Label>
                <Input
                  id="edit_teacher_name"
                  name="teacher_name"
                  value={formData.teacher_name}
                  onChange={handleFormChange}
                  placeholder="Enter teacher name"
                />
              </div>

              {/* Total Fees */}
              <div>
                <Label htmlFor="edit_total_fees">Total Fees (₹)</Label>
                <Input
                  id="edit_total_fees"
                  name="total_fees"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.total_fees}
                  onChange={handleFormChange}
                  placeholder="Enter total fees"
                />
              </div>

              {/* Paid Fees */}
              <div>
                <Label htmlFor="edit_paid_fees">Paid Fees (₹)</Label>
                <Input
                  id="edit_paid_fees"
                  name="paid_fees"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.paid_fees}
                  onChange={handleFormChange}
                  placeholder="Enter paid amount"
                />
              </div>
            </div>

            {/* Due Fees Display */}
            {(formData.total_fees || formData.paid_fees) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Due Fees:</span>
                  <span className="text-xl font-bold text-blue-600">
                    ₹{(parseFloat(formData.total_fees || 0) - parseFloat(formData.paid_fees || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* Address */}
            <div>
              <Label htmlFor="edit_address">Address (Optional)</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Textarea
                  id="edit_address"
                  name="address"
                  value={formData.address}
                  onChange={handleFormChange}
                  placeholder="Enter complete address"
                  rows={3}
                  className="pl-10"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={submitting}
              >
                {submitting ? 'Updating...' : 'Update Student'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this student?
            </DialogDescription>
          </DialogHeader>
          {selectedStudent && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
              <p className="text-sm font-semibold text-red-900 mb-2">
                Student: {selectedStudent.student_name}
              </p>
              <p className="text-sm text-red-700 mb-1">
                Class: {selectedStudent.class_name}
              </p>
              <p className="text-sm text-red-700">
                Parent: {selectedStudent.parent_name}
              </p>
              <div className="mt-3 pt-3 border-t border-red-300">
                <p className="text-xs text-red-800 font-semibold">
                  ⚠️ This will also delete:
                </p>
                <ul className="text-xs text-red-700 mt-1 space-y-1 list-disc list-inside">
                  <li>All homework submissions</li>
                  <li>All attendance records</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700"
              onClick={handleConfirmDelete}
              disabled={submitting}
            >
              {submitting ? 'Deleting...' : 'Delete Student'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentsManagementPage;
